import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { fetchJourney } from '../../../services/timeline';
import { patientTimelineQuery } from '../../../hooks/patientTimelineQuery';
import type { TimelineEvent } from '../actions/types';
import { TimelineRailCollapsed } from './TimelineRailCollapsed';
import { TimelineRailExpanded } from './TimelineRailExpanded';
import { useSideRailCollapsed } from '../../side-rail/useSideRailCollapsed';
import { type TimelineMode } from './TimelineModeToggle';

const TIMELINE_RAIL_STALE_MS = 60_000;
// Bump the key suffix whenever the default changes, so stale stored preferences are ignored.
const TIMELINE_COLLAPSED_STORAGE_KEY = 'odo_files_viewer_timeline_collapsed_v2';
const TIMELINE_DEFAULT_COLLAPSED = true;

interface TimelineRailProps {
    patientId: string;
}

function sortNewestFirst(events: TimelineEvent[]): TimelineEvent[] {
    return [...events].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
}

function computeCaseAgeLabel(events: TimelineEvent[], total: number): string | null {
    if (events.length === 0 || events.length < total) return null;
    const oldest = events[events.length - 1];
    if (!oldest) return null;
    const opened = new Date(oldest.createdAt).getTime();
    if (Number.isNaN(opened)) return null;
    const days = Math.max(0, Math.floor((Date.now() - opened) / (24 * 60 * 60 * 1000)));
    if (days === 0) return 'Opened today';
    if (days === 1) return '1 day';
    return `${days} days`;
}

function TimelineRail({ patientId }: TimelineRailProps) {
    const { collapsed, toggle } = useSideRailCollapsed(
        TIMELINE_COLLAPSED_STORAGE_KEY,
        TIMELINE_DEFAULT_COLLAPSED
    );
    const [mode, setMode] = useState<TimelineMode>('activity');

    const { data, isLoading, isError } = useQuery({
        ...patientTimelineQuery(patientId),
        staleTime: TIMELINE_RAIL_STALE_MS,
        enabled: Boolean(patientId)
    });

    // Journey (steps/phases + durations) is fetched lazily, only once the user
    // switches the rail to journey mode.
    const journeyQuery = useQuery({
        queryKey: ['patient-journey', patientId],
        queryFn: () => fetchJourney(patientId),
        staleTime: TIMELINE_RAIL_STALE_MS,
        enabled: Boolean(patientId) && mode === 'journey'
    });

    const events = useMemo(() => sortNewestFirst(data?.data ?? []), [data]);
    const total = data?.total ?? events.length;
    const caseAgeLabel = useMemo(() => computeCaseAgeLabel(events, total), [events, total]);

    if (collapsed) {
        return <TimelineRailCollapsed events={events} total={total} onExpand={toggle} />;
    }

    return (
        <TimelineRailExpanded
            events={events}
            total={total}
            isLoading={isLoading}
            isError={isError}
            caseAgeLabel={caseAgeLabel}
            onCollapse={toggle}
            mode={mode}
            onModeChange={setMode}
            journeySegments={journeyQuery.data ?? []}
            journeyLoading={journeyQuery.isLoading}
            journeyError={journeyQuery.isError}
        />
    );
}

export { TimelineRail };
